import React from "react";
import { Box, Typography, Button, Fade } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LockIcon from "@mui/icons-material/Lock";
import { keyframes } from "@mui/system";

/* ---------------------------------------------------
   Pulse Animation for the Lock Icon
--------------------------------------------------- */
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
  100% { transform: scale(1); opacity: 1; }
`;

/* ---------------------------------------------------
  Smooth Fade-In Up Animation
--------------------------------------------------- */
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(25px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          position: "relative",
          overflow: "hidden",
          bgcolor: "background.default",
        }}
      >
        {/* Background gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background:
              "linear-gradient(135deg, rgba(11,17,32,0.9) 0%, rgba(37,99,235,0.25) 100%)",
            opacity: 0.85,
          }}
        />

        {/* Content container */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            animation: `${fadeInUp} 0.9s ease`,
          }}
        >
          {/* Animated Lock Icon */}
          <LockIcon
            sx={{
              fontSize: 90,
              mb: 2,
              color: "error.main",
              animation: `${pulse} 1.6s infinite ease-in-out`,
            }}
          />

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "text.primary",
            }}
          >
            Access Denied
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              maxWidth: 420,
              color: "text.secondary",
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            You don’t have permission to access this page.  
            If you believe this is an error, please contact your administrator.
          </Typography>

          {/* Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/")}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 3,
                fontWeight: 600,
                boxShadow: "0 4px 20px rgba(59,130,246,0.3)",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              Back to Dashboard
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate("/logout")}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 3,
                fontWeight: 600,
                borderColor: "text.secondary",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default UnauthorizedPage;
